// src/pages/AdminDashboard.js
// Fase 12: Panel Admin — Navegación 2 niveles (4 categorías + sub-items)
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { ADMIN_COLORS } from '../components/admin/AdminShared';

// Componentes del panel
import AdminDashboardStats from '../components/admin/AdminDashboardStats';
import AdminUserManagement from '../components/admin/AdminUserManagement';
import AdminGruposContenido from '../components/admin/AdminGruposContenido';
import AdminDocumentosPage from './admin/AdminDocumentosPage';
import AdminNotificaciones from '../components/admin/AdminNotificaciones';
import VoiceServiceAdmin from '../components/admin/VoiceServiceAdmin';
import AdminSeguimiento from '../components/admin/AdminSeguimiento';
import AdminProyectos from '../components/admin/AdminProyectos';
import AdminWhatsAppTraining from '../components/admin/AdminWhatsAppTraining';
import AdminAjustarProgreso from '../components/admin/AdminAjustarProgreso';
import AdminMediaLibrary from '../components/admin/AdminMediaLibrary';

// ============================================================
// SVG ICONS (Feather-style)
// ============================================================

const IconDashboard = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
  </svg>
);

const IconContenido = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const IconGestion = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconCanales = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </svg>
);

// ============================================================
// NAVIGATION STRUCTURE
// ============================================================

const NAV_CATEGORIES = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: IconDashboard,
    items: null, // no sub-items, renders directly
  },
  {
    id: 'contenido',
    label: 'Contenido',
    icon: IconContenido,
    items: [
      { id: 'documents', label: 'Contenidos' },
      { id: 'media-library', label: 'Biblioteca' },
      { id: 'groups', label: 'Grupos Contenido' },
    ],
  },
  {
    id: 'gestion',
    label: 'Gestión',
    icon: IconGestion,
    items: [
      { id: 'users', label: 'Usuarios' },
      { id: 'progreso', label: 'Ajustar Progreso' },
      { id: 'proyectos', label: 'Proyectos' },
      { id: 'seguimiento', label: 'Seguimiento' },
    ],
  },
  {
    id: 'canales',
    label: 'Canales',
    icon: IconCanales,
    items: [
      { id: 'wa-training', label: 'WA Training' },
      { id: 'notificaciones', label: 'Notificaciones' },
      { id: 'config', label: 'Configuración' },
    ],
  },
];

// ============================================================
// STYLED COMPONENTS
// ============================================================

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  color: ${ADMIN_COLORS.primary};
  margin: 0;
  font-size: 1.6rem;
`;

const CategoryBar = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 0;
  background: ${ADMIN_COLORS.primary};
  border-radius: 12px 12px 0 0;
  padding: 0.5rem 0.5rem 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar { display: none; }
`;

const CategoryTab = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.25rem;
  border: none;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  font-weight: ${p => p.$active ? '700' : '500'};
  font-size: 0.9rem;
  color: ${p => p.$active ? ADMIN_COLORS.primary : 'rgba(255,255,255,0.7)'};
  background: ${p => p.$active ? ADMIN_COLORS.white : 'transparent'};
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    color: ${p => p.$active ? ADMIN_COLORS.primary : '#fff'};
    background: ${p => p.$active ? ADMIN_COLORS.white : 'rgba(255,255,255,0.1)'};
  }

  svg { flex-shrink: 0; }
`;

const SubBar = styled.div`
  display: flex;
  gap: 0.25rem;
  background: ${ADMIN_COLORS.white};
  border-bottom: 2px solid ${ADMIN_COLORS.border};
  padding: 0.5rem 0.75rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 0 12px 0 0;

  &::-webkit-scrollbar { display: none; }
`;

const SubTab = styled.button`
  padding: 0.45rem 1rem;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-weight: ${p => p.$active ? '600' : '400'};
  font-size: 0.85rem;
  color: ${p => p.$active ? ADMIN_COLORS.white : ADMIN_COLORS.textLight};
  background: ${p => p.$active ? ADMIN_COLORS.accent : 'transparent'};
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    color: ${p => p.$active ? ADMIN_COLORS.white : ADMIN_COLORS.accent};
    background: ${p => p.$active ? ADMIN_COLORS.accent : '#f0fdfa'};
  }
`;

const ContentArea = styled.div`
  padding-top: 1.5rem;
`;

// ============================================================
// COMPONENT MAP
// ============================================================

const COMPONENT_MAP = {
  dashboard: AdminDashboardStats,
  users: AdminUserManagement,
  groups: AdminGruposContenido,
  documents: ({ embedded }) => <AdminDocumentosPage embedded />,
  'media-library': AdminMediaLibrary,
  seguimiento: AdminSeguimiento,
  proyectos: AdminProyectos,
  'wa-training': AdminWhatsAppTraining,
  notificaciones: AdminNotificaciones,
  progreso: AdminAjustarProgreso,
  config: VoiceServiceAdmin,
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const AdminDashboard = () => {
  const { user } = useAuth();
  const [activeCategory, setActiveCategory] = useState('dashboard');
  const [activeItem, setActiveItem] = useState(null);

  if (!user || user.role !== 'admin') {
    return <Navigate to="/contenidos" />;
  }

  const currentCategory = NAV_CATEGORIES.find(c => c.id === activeCategory);
  const hasSubItems = currentCategory?.items && currentCategory.items.length > 0;

  // Determine which component to render
  const currentItemId = hasSubItems ? (activeItem || currentCategory.items[0].id) : activeCategory;
  const ActiveComponent = COMPONENT_MAP[currentItemId];

  const handleCategoryClick = (catId) => {
    setActiveCategory(catId);
    const cat = NAV_CATEGORIES.find(c => c.id === catId);
    if (cat?.items) {
      setActiveItem(cat.items[0].id);
    } else {
      setActiveItem(null);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Panel de Administración</Title>
      </Header>

      <CategoryBar>
        {NAV_CATEGORIES.map(cat => {
          const Icon = cat.icon;
          return (
            <CategoryTab
              key={cat.id}
              $active={activeCategory === cat.id}
              onClick={() => handleCategoryClick(cat.id)}
            >
              <Icon />
              {cat.label}
            </CategoryTab>
          );
        })}
      </CategoryBar>

      {hasSubItems && (
        <SubBar>
          {currentCategory.items.map(item => (
            <SubTab
              key={item.id}
              $active={(activeItem || currentCategory.items[0].id) === item.id}
              onClick={() => setActiveItem(item.id)}
            >
              {item.label}
            </SubTab>
          ))}
        </SubBar>
      )}

      <ContentArea>
        {ActiveComponent && (
          typeof ActiveComponent === 'function' && ActiveComponent.prototype?.isReactComponent
            ? <ActiveComponent />
            : typeof ActiveComponent === 'function'
              ? <ActiveComponent />
              : null
        )}
      </ContentArea>
    </Container>
  );
};

export default AdminDashboard;
